import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye, Users, BookOpen, Calendar, Shield, Award, ArrowRight,
  CheckCircle2, Phone, Mail, MapPin, FileText, ShoppingCart,
  GraduationCap, Briefcase, Heart, Star, Globe, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useTenant } from "../context/TenantContext";

const ICON_MAP = {
  Eye, Users, BookOpen, Calendar, Shield, Award, FileText,
  ShoppingCart, GraduationCap, Briefcase, Heart, Star, Globe, Zap,
  Phone, Mail, MapPin
};

const getIcon = (name) => ICON_MAP[name] || FileText;

const LandingPage = () => {
  const { config, landing, servicios, beneficios, contacto, footer, tenantLoading } = useTenant();

  if (tenantLoading || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: "var(--color-primary, #2853B9)" }}></div>
      </div>
    );
  }

  const cp = config.color_primario;
  const cs = config.color_secundario;
  const ca = config.color_acento;
  const cc = config.color_cta;
  const HeroIcon = getIcon(landing?.hero_badge_icono);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {config.logo_url && <img src={config.logo_url} alt={config.nombre} className="h-12 object-contain" />}
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#sobre" className="text-gray-600 hover:opacity-80 transition-colors font-medium" style={{ "--tw-text-opacity": 1 }}>
              {landing?.about_titulo ? "Sobre nosotros" : "Servicios"}
            </a>
            <a href="#beneficios" className="text-gray-600 hover:opacity-80 transition-colors font-medium">
              Beneficios
            </a>
            <a href="#contacto" className="text-gray-600 hover:opacity-80 transition-colors font-medium">
              Contacto
            </a>
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-700" data-testid="login-btn">Iniciar Sesion</Button>
            </Link>
            <Link to="/register">
              <Button style={{ backgroundColor: cp }} className="text-white rounded-full px-6 hover:opacity-90" data-testid="register-btn">
                {landing?.hero_cta_texto || "Registrarse"}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 hero-pattern overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {landing?.hero_badge_texto && (
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
                  style={{ backgroundColor: cp + "15", color: cp }}>
                  <HeroIcon className="w-4 h-4 mr-2" />
                  {landing.hero_badge_texto}
                </span>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ color: cs }}>
                {landing?.hero_titulo}{" "}
                {landing?.hero_titulo_resaltado && (
                  <span style={{ color: ca }}>{landing.hero_titulo_resaltado}</span>
                )}
              </h1>
              {landing?.hero_subtitulo && (
                <p className="text-lg text-gray-600 mb-8 max-w-xl">{landing.hero_subtitulo}</p>
              )}
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:opacity-90 transition-all"
                    style={{ backgroundColor: cc }} data-testid="hero-register-btn">
                    {landing?.hero_cta_texto || "Registrarse"}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" size="lg" className="rounded-full px-8 py-6 text-lg border-gray-200 hover:bg-gray-50"
                    data-testid="hero-login-btn">
                    {landing?.hero_cta_login_texto || "Ya tengo cuenta"}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {landing?.hero_imagen_url && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img src={landing.hero_imagen_url} alt={config.nombre} className="w-full h-[500px] object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, " + cs + "66, transparent)" }}></div>
                  {landing?.hero_stat_texto && (
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: cp }}>
                            <Award className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold" style={{ color: cs }}>{landing.hero_stat_texto}</p>
                            <p className="text-sm text-gray-500">{landing.hero_stat_subtexto}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      {servicios.length > 0 && (
        <section id="sobre" className="py-20 bg-[#F6F8FA]">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: cs }}>
                {landing?.about_titulo || "Nuestros Servicios"}
              </h2>
              {landing?.about_subtitulo && <p className="text-gray-600 max-w-2xl mx-auto">{landing.about_subtitulo}</p>}
            </motion.div>
            <div className={"grid md:grid-cols-2 gap-6 " + (servicios.length >= 4 ? "lg:grid-cols-4" : servicios.length === 3 ? "lg:grid-cols-3" : "")}>
              {servicios.map((svc, index) => {
                const SvcIcon = getIcon(svc.icono);
                return (
                  <motion.div key={svc.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="cao-card p-6 card-hover">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: cp + "15" }}>
                      <SvcIcon className="w-7 h-7" style={{ color: cp }} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: cs }}>{svc.titulo}</h3>
                    <p className="text-gray-600 text-sm">{svc.descripcion}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Benefits */}
      {beneficios.length > 0 && (
        <section id="beneficios" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: cs }}>
                  {landing?.beneficios_titulo || "Beneficios"}
                </h2>
                {landing?.beneficios_subtitulo && <p className="text-gray-600 mb-8">{landing.beneficios_subtitulo}</p>}
                <ul className="space-y-4">
                  {beneficios.map((b, index) => (
                    <motion.li key={b.id} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{b.texto}</span>
                    </motion.li>
                  ))}
                </ul>
                <Link to="/register" className="inline-block mt-8">
                  <Button className="text-white rounded-full px-8 hover:opacity-90" style={{ backgroundColor: cp }}
                    data-testid="benefits-register-btn">
                    {landing?.cta_boton_texto || "Comenzar"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
              {landing?.beneficios_imagen_url && (
                <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                  <img src={landing.beneficios_imagen_url} alt="Beneficios" className="rounded-2xl shadow-xl" />
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {landing?.cta_titulo && (
        <section className="py-20" style={{ backgroundColor: cs }}>
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <HeroIcon className="w-16 h-16 mx-auto mb-6" style={{ color: ca }} />
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{landing.cta_titulo}</h2>
              {landing.cta_subtitulo && <p className="mb-8 max-w-2xl mx-auto" style={{ color: ca + "AA" }}>{landing.cta_subtitulo}</p>}
              <Link to="/register">
                <Button size="lg" className="text-white rounded-full px-10 py-6 text-lg font-semibold hover:opacity-90"
                  style={{ backgroundColor: cc }} data-testid="cta-register-btn">
                  {landing.cta_boton_texto || "Comenzar"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Contact */}
      {contacto && (
        <section id="contacto" className="py-20 bg-[#F6F8FA]">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4" style={{ color: cs }}>Contacto</h2>
              <p className="text-gray-600">Estamos aqui para ayudarle</p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8">
              {contacto.telefono && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="cao-card p-6 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: cp + "15" }}>
                    <Phone className="w-6 h-6" style={{ color: cp }} />
                  </div>
                  <h3 className="font-semibold mb-2" style={{ color: cs }}>Telefono</h3>
                  <p className="text-gray-600">{contacto.telefono}</p>
                </motion.div>
              )}
              {contacto.email && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="cao-card p-6 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: cp + "15" }}>
                    <Mail className="w-6 h-6" style={{ color: cp }} />
                  </div>
                  <h3 className="font-semibold mb-2" style={{ color: cs }}>Email</h3>
                  <p className="text-gray-600">{contacto.email}</p>
                </motion.div>
              )}
              {contacto.direccion_linea1 && (
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="cao-card p-6 text-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: cp + "15" }}>
                    <MapPin className="w-6 h-6" style={{ color: cp }} />
                  </div>
                  <h3 className="font-semibold mb-2" style={{ color: cs }}>Direccion</h3>
                  <p className="text-gray-600">{contacto.direccion_linea1}</p>
                  {contacto.direccion_linea2 && <p className="text-gray-600">{contacto.direccion_linea2}</p>}
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: cs }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {config.logo_url && <img src={config.logo_url} alt={config.nombre} className="h-10 brightness-0 invert" />}
              <span className="text-sm" style={{ color: ca + "CC" }}>
                {footer?.copyright_texto ? "\u00A9 " + footer.copyright_texto : "\u00A9 " + config.nombre}
              </span>
            </div>
            {footer?.powered_by_logo && (
              <div className="flex items-center gap-2 text-xs" style={{ color: ca + "99" }}>
                <span>Powered by</span>
                <a href={footer.powered_by_url || "#"} target="_blank" rel="noopener noreferrer">
                  <img src={footer.powered_by_logo} alt="Powered by" className="h-6 hover:opacity-80 transition-opacity" />
                </a>
              </div>
            )}
            {footer?.website_url && (
              <div className="flex items-center gap-6 text-sm" style={{ color: ca + "CC" }}>
                <a href={footer.website_url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  {footer.website_label || footer.website_url}
                </a>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
